/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Helper functions for clearing component states in Cloud Propagation modal
 */

// Interface for VRF form data
export interface VRFFormData {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[];
  enis: string[];
  expanded: boolean;
  selectedVpc?: string;
  persistence?: boolean; // Hold last-known route in the cloud when the peer goes down
  // Next-hop resolution retry — "Advanced settings → Retry" (CTX-181)
  nexthopRetryEnabled?: boolean;
  nexthopRetryIntervalSeconds?: number;
  nexthopRetryAttempts?: number;
}

/**
 * Clear ENI selection for both Telco and Enterprise modes
 */
export const clearEniSelection = (
  isTelco: boolean,
  vrfIndex: number | undefined,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void,
  values: any,
  setValues: (values: any) => void
) => {
  if (isTelco && vrfIndex !== undefined) {
    // For Telco mode, clear specific VRF's ENI
    setVrfs(vrfs.map((item, i) => 
      i === vrfIndex 
        ? { ...item, selectedEni: "", enis: [] }
        : item
    ));
  } else {
    // For Enterprise mode, clear main values
    setValues({ 
      ...values, 
      selectedEni: "",
      enis: []
    });
  }
};

/**
 * Remove routing table from array for both Telco and Enterprise modes
 */
export const removeRoutingTable = (
  routingTable: string,
  isTelco: boolean,
  vrfIndex: number | undefined,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void,
  values: any,
  setValues: (values: any) => void
) => {
  if (isTelco && vrfIndex !== undefined) {
    // For Telco mode, remove from specific VRF
    setVrfs(vrfs.map((vrf, i) => 
      i === vrfIndex 
        ? { ...vrf, routingTables: vrf.routingTables.filter(rt => rt !== routingTable) }
        : vrf
    ));
  } else {
    // For Enterprise mode, remove from main values
    setValues({ 
      ...values, 
      routingTables: values.routingTables?.filter((rt: string) => rt !== routingTable) || []
    });
  }
};

/**
 * Remove ENI from array for both Telco and Enterprise modes
 */
export const removeEni = (
  eni: string,
  isTelco: boolean,
  vrfIndex: number | undefined,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void,
  values: any,
  setValues: (values: any) => void
) => {
  if (isTelco && vrfIndex !== undefined) {
    // For Telco mode, remove from specific VRF
    setVrfs(vrfs.map((vrf, i) => 
      i === vrfIndex 
        ? { ...vrf, enis: vrf.enis.filter(e => e !== eni) }
        : vrf
    ));
  } else {
    // For Enterprise mode, remove from main values
    setValues({ 
      ...values, 
      enis: values.enis?.filter((e: string) => e !== eni) || []
    });
  }
};

/**
 * Remove route target from VRF
 */
export const removeRouteTarget = (
  vrfIndex: number,
  targetIndex: number,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void
) => {
  setVrfs(vrfs.map((item, i) => 
    i === vrfIndex 
      ? { ...item, routeTargets: item.routeTargets.filter((_, j) => j !== targetIndex) }
      : item
  ));
};

/**
 * Clear all routing tables for a specific VRF or main values
 */
export const clearAllRoutingTables = (
  isTelco: boolean,
  vrfIndex: number | undefined,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void,
  values: any,
  setValues: (values: any) => void
) => {
  if (isTelco && vrfIndex !== undefined) {
    // For Telco mode, clear specific VRF's routing tables
    setVrfs(vrfs.map((item, i) => 
      i === vrfIndex 
        ? { ...item, routingTables: [], selectedRoutingTable: "" }
        : item
    ));
  } else {
    // For Enterprise mode, clear main values
    setValues({ 
      ...values, 
      routingTables: [],
      selectedRoutingTable: ""
    });
  }
};

/**
 * Clear all ENIs for a specific VRF or main values
 */
export const clearAllEnis = (
  isTelco: boolean,
  vrfIndex: number | undefined,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void,
  values: any,
  setValues: (values: any) => void
) => {
  if (isTelco && vrfIndex !== undefined) {
    // For Telco mode, clear specific VRF's ENIs
    setVrfs(vrfs.map((item, i) => 
      i === vrfIndex 
        ? { ...item, enis: [], selectedEni: "" }
        : item
    ));
  } else {
    // For Enterprise mode, clear main values
    setValues({ 
      ...values, 
      enis: [],
      selectedEni: ""
    });
  }
};

/**
 * Clear all selections (routing tables and ENIs) for a specific VRF or main values
 */
export const clearAllSelections = (
  isTelco: boolean,
  vrfIndex: number | undefined,
  vrfs: VRFFormData[],
  setVrfs: (vrfs: VRFFormData[]) => void,
  values: any,
  setValues: (values: any) => void
) => {
  if (isTelco && vrfIndex !== undefined) {
    // For Telco mode, clear specific VRF's selections
    setVrfs(vrfs.map((item, i) => 
      i === vrfIndex 
        ? { 
            ...item, 
            routingTables: [], 
            selectedRoutingTable: "",
            enis: [], 
            selectedEni: "" 
          }
        : item
    ));
  } else {
    // For Enterprise mode, clear main values
    setValues({ 
      ...values, 
      routingTables: [],
      selectedRoutingTable: "",
      enis: [],
      selectedEni: ""
    });
  }
};
